'use client';

import { ChangeEvent, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import useCustomRouter from '@/hooks/useCustomRouter';
import PasswordInput from '@/components/atoms/password-input';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import Typography from '@/components/atoms/typography';
import toast from '@/components/atoms/toaster/helpers/toast';
import AuthLayout from '../layouts/auth-layout';
import { ResetPasswordData, ResetPasswordProps } from './types';
import AuthForm from '../layouts/auth-form';

const ResetPassword = ({ logo, logoLink, resetPassword }: ResetPasswordProps) => {
  const router = useCustomRouter();

  const searchParams = useSearchParams();

  const { translate } = useTranslation();

  const [reset, setReset] = useState(false);
  const [data, setData] = useState<ResetPasswordData>({
    password: '',
    confirmPassword: '',
  });

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target;
    setData({ ...data, [name]: value });
  };

  const handleSubmit = async () => {
    const token = searchParams.get('token');

    if (reset) {
      router.push('/');
      return;
    }

    if (token && data.password == data.confirmPassword) {
      const resetResponse = await resetPassword(token, data.password);
      if (typeof resetResponse === 'string') {
        toast.error(resetResponse);
        return;
      }

      setReset(true);
    }
  };

  const isPasswordValid = data.password && data.confirmPassword && data.password === data.confirmPassword;

  return (
    <AuthLayout logo={logo} logoLink={logoLink}>
      <AuthForm
        onSubmit={handleSubmit}
        includeCheckIcon={reset}
        isButtonDisabled={!isPasswordValid}
        title={
          reset ? translate('account.password.reset.success.headline') : translate('account.password.reset.headline')
        }
        buttonLabel={reset ? translate('account.account.login') : translate('account.password.reset.keyword')}
        footerLinkLabel={reset ? '' : translate('account.account.back.login')}
        footerLink="/login"
      >
        {reset ? (
          <Typography fontSize={16} className="inline text-gray-600" lineHeight="loose">
            {translate('account.password.reset.success.desc')}
          </Typography>
        ) : (
          <>
            <PasswordInput
              containerClassName="w-full"
              className="w-full"
              name="password"
              label={translate('account.password')}
              value={data.password}
              onChange={handleChange}
            />
            <PasswordInput
              containerClassName="w-full"
              className="w-full"
              name="confirmPassword"
              label={translate('account.password.confirm')}
              value={data.confirmPassword}
              onChange={handleChange}
            />
            {!isPasswordValid && data.confirmPassword && (
              <Typography fontSize={14} className="mt-3 text-red-500">
                {translate('account.password.confirm.error')}
              </Typography>
            )}
          </>
        )}
      </AuthForm>
    </AuthLayout>
  );
};

export default ResetPassword;
